import { FLAGS } from '@iheartjane/feature-flags';
import {
  Button,
  Flex,
  PremiumBagIcon,
  PremiumBullseyeIcon,
  PremiumPhoneIcon,
  ProfileIcon,
  Typography,
} from '@iheartjane/reefer';

import { LinkWithBasename } from '@jane/bloom/components/linkWithBasename';
import { useRouteWithRedirect } from '@jane/bloom/hooks/useRedirectRoute';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import {
  getSignInPromptDefaults,
  resolveSignInPromptColors,
} from '@jane/bloom/resources/signInPromptTheme';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';

import styles from './accountInfo.module.css';
import { Prompt } from './prompt/prompt';

type AccountInfoParams = {
  disabled?: boolean;
  disableRedirect?: boolean;
  dismissPrompt?: () => void;
  showPrompt?: boolean;
};

export const AccountInfo = ({
  disabled,
  disableRedirect,
  dismissPrompt,
  showPrompt = false,
}: AccountInfoParams) => {
  const {
    featureFlags,
    pinnedStore,
    shoppingPreferences,
    theme,
    whoami: {
      user: { email: userEmail },
    },
  } = useRootLoaderData();
  const getRouteWithRedirect = useRouteWithRedirect();

  const customizeEnabled = featureFlags[FLAGS.c1CustomizeLoginPrompt];
  const lp = customizeEnabled ? theme.bloom?.login_prompt : undefined;
  const defaults = getSignInPromptDefaults(
    pinnedStore?.crm_integration === 'alpineiq'
  );

  // Use || (not ??) for string fallbacks so empty strings fall through to
  // defaults instead of rendering blank.
  const header = lp?.header || defaults.header;
  const line1 = lp?.desktop_line_1 || defaults.desktop_line_1;
  const line2 = lp?.desktop_line_2 || defaults.desktop_line_2;
  const line3 = lp?.desktop_line_3 || defaults.desktop_line_3;
  const ctaLabel = lp?.cta_label || defaults.cta_label;
  const { button, buttonText } = resolveSignInPromptColors(theme);

  const loginButtonElement = (
    <LinkWithBasename
      aria-label={userEmail ? 'Account' : 'Sign in'}
      to={getRouteWithRedirect(
        userEmail ? '/account' : '/login',
        !!userEmail || !!disableRedirect
      )}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
        }

        trackHeaderTap(
          {
            reservationMode: shoppingPreferences?.reservationMode,
            tappable: 'profile icon',
          },
          featureFlags
        );
      }}
    >
      <Button.Icon
        icon={<ProfileIcon color="text-main" />}
        type="button"
        variant="minimal"
        className={styles['profileIcon']}
      />
    </LinkWithBasename>
  );

  if (showPrompt && dismissPrompt) {
    return (
      <Prompt
        dismissible
        dismissPrompt={dismissPrompt}
        showPrompt={showPrompt}
        target={loginButtonElement}
      >
        <Prompt.Content>
          <Flex
            flexDirection="column"
            gap={16}
            className={styles['promptIcons']}
          >
            <Typography variant="body-bold">{header}</Typography>
            <Flex gap={16}>
              <PremiumPhoneIcon size="lg" color="inherit" />
              <Typography>{line1}</Typography>
            </Flex>
            <Flex gap={16}>
              <PremiumBullseyeIcon size="lg" color="inherit" />
              <Typography>{line2}</Typography>
            </Flex>
            <Flex gap={16}>
              <PremiumBagIcon size="lg" color="inherit" />
              <Typography>{line3}</Typography>
            </Flex>
            <LinkWithBasename to="/login" className={styles['ctaLink']}>
              <Button
                variant="primary"
                full
                label={ctaLabel}
                style={
                  customizeEnabled
                    ? {
                        backgroundColor: button,
                        borderColor: button,
                        color: buttonText,
                      }
                    : undefined
                }
              />
            </LinkWithBasename>
          </Flex>
        </Prompt.Content>
      </Prompt>
    );
  }

  return loginButtonElement;
};
